import Overlay from "components/Signup/components/Overlay";
import { FC, ReactElement, ReactNode, useEffect, useState } from "react";
import { useWebTranslation, Trans } from "translations";
import Gutter from "ui-kit/Gutter";
import Icon from "ui-kit/Icon";
import { FlexRow } from "ui-kit/Layout/Flex";
import { Typography } from "ui-kit/Typography";
import Colors from "ui-kit/colors";
import { PaymentMethodState } from "../usePaymentMethodState";
import styles from "./TrustlyComponent.module.css";
import TrustlySecure from "./TrustlySecure";
import { SelectablePaymentMethodEnum } from "config/site";

interface TrustlyComponentProps {
  paymentMethod: SelectablePaymentMethodEnum;
  setPaymentMethodState: ({ isValid, data }: PaymentMethodState) => void;
}

const TrustlyDescription = () => {
  const { t } = useWebTranslation();

  return (
    <>
      <Typography>{t("payment_methods.trustly_info_benefits")}</Typography>
      <Gutter size={2} />
      <ul className={styles.list}>
        <li className={styles.item}>
          <Typography>{t("payment_methods.trustly_info_item_1")}</Typography>
        </li>
        <Gutter size={1} />
        <li className={styles.item}>
          <Typography>{t("payment_methods.trustly_info_item_2")}</Typography>
        </li>
        <Gutter size={1} />
        <li className={styles.item}>
          <Typography>{t("payment_methods.trustly_info_item_3")}</Typography>
        </li>
      </ul>
      <Gutter size={2} />
      <Typography>{t("payment_methods.trustly_info_secure")}</Typography>
      <Gutter size={2} />
      <Typography>{t("payment_methods.trustly_info_read_more")}</Typography>
    </>
  );
};

const TrustlyComponent: FC<TrustlyComponentProps> = ({
  paymentMethod,
  setPaymentMethodState,
}): ReactElement => {
  const { t } = useWebTranslation();
  const [overlayDisplay, setOverlayDisplay] = useState<{
    title: string;
    content: ReactNode;
    full?: boolean;
  } | null>(null);

  useEffect(() => {
    if (
      [
        SelectablePaymentMethodEnum.Trustly,
        SelectablePaymentMethodEnum.TrustlyMdd,
      ].includes(paymentMethod)
    )
      setPaymentMethodState({
        isValid: true,
        data: { returnUrl: window.location.href.split("?")[0] },
      });
  }, [paymentMethod, setPaymentMethodState]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
      }}
    >
      <Typography variant="body3">
        <strong>{t("payment_methods.trustly_info_title")}</strong>
      </Typography>
      <Gutter size={2} />
      <FlexRow style={{ gap: 4, marginLeft: -4 }}>
        <Icon name="checkmark_small" size={16} color={Colors.green200} />
        <Typography style={{ fontSize: 14, alignSelf: "center" }}>
          {t("payment_methods.trustly_info_item_1")}
        </Typography>
      </FlexRow>
      <Gutter size={1} />
      <FlexRow style={{ gap: 4, marginLeft: -4 }}>
        <Icon name="checkmark_small" size={16} color={Colors.green200} />
        <Typography style={{ fontSize: 14, alignSelf: "center" }}>
          {t("payment_methods.trustly_info_item_2")}
        </Typography>
      </FlexRow>
      <Gutter size={2} />
      <div
        role="button"
        onClick={() =>
          setOverlayDisplay({
            title: t("payment_methods.trustly_info_title"),
            content: <TrustlyDescription />,
            full: true,
          })
        }
      >
        <Typography variant="body3">
          <Trans i18nKey="payment_methods.trustly_info" />
        </Typography>
      </div>
      <Gutter size={2} />
      <TrustlySecure />
      <Overlay
        display={overlayDisplay}
        onRemove={() => setOverlayDisplay(null)}
      />
    </div>
  );
};

export default TrustlyComponent;
